import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import RuleIcon from "@mui/icons-material/Rule";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ScheduleIcon from "@mui/icons-material/Schedule";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { tokens } from "../../../theme";
import { useAppContext } from "../../../contexts/AppContext";
import {
  computeSelectionSignature,
  isPreCheckStale,
} from "../../../utils/mockSchemaValidation";
import { runSchemaValidation } from "../../../utils/schemaValidation";
import { StatusBadge } from "../../../components/states";
import {
  getFindingDisplay,
  sortFindingsBySeverity,
} from "../../../utils/findingDisplay";

/**
 * Configuration page — optional Schema Pre-check block.
 *
 * Advisory only. Does NOT block Confirm and Save. Does NOT replace the
 * mandatory final validation gate on /execute Start.
 *
 * State derivation (see proposal §State model):
 *   not_ready: selection incomplete → button disabled
 *   not_checked: no result stored yet
 *   checking: synchronous mock right now → momentary
 *   passed / failed: from last stored result
 *   stale: stored result exists but current signature differs
 */
const SchemaPreCheckPanel = ({ processingType }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const {
    selectedSites,
    siteCapabilities,
    sites,
    schemaPreCheck,
    setSchemaPreCheck,
  } = useAppContext();

  const [showDetails, setShowDetails] = useState(false);

  const completed = (selectedSites || []).filter((s) => s && s.site_id && s.capability_id);
  const currentSignature = useMemo(
    () => computeSelectionSignature(processingType, selectedSites),
    [processingType, selectedSites]
  );

  // Derive expected schema heuristically — first cap's dataset_ref (matches
  // what the validator will compute when selections are non-mixed).
  const expectedSchema = useMemo(() => {
    const firstCap =
      completed.length > 0
        ? (siteCapabilities?.[completed[0].site_id] || []).find(
            (c) => c.capability_id === completed[0].capability_id
          )
        : null;
    return firstCap?.dataset_ref || null;
  }, [completed, siteCapabilities]);

  // Compute panel state. Three severities (passed / warn / failed) mirror the
  // backend data-review model; warn is non-blocking.
  let state = "not_ready";
  if (completed.length > 0) {
    state = "not_checked";
    if (schemaPreCheck?.result) {
      const stale = isPreCheckStale(currentSignature, schemaPreCheck.signature);
      if (stale) {
        state = "stale";
      } else {
        const overall =
          schemaPreCheck.result.overall ||
          (schemaPreCheck.result.passed ? "pass" : "fail");
        state = overall === "pass" ? "passed" : overall === "warn" ? "warn" : "failed";
      }
    }
    if (schemaPreCheck?.status === "checking") state = "checking";
  }

  const runPreCheck = async () => {
    setSchemaPreCheck({ ...schemaPreCheck, status: "checking" });
    // Tiny delay so the "Checking…" state is visible in mock mode (the real
    // backend path adds its own network latency). runSchemaValidation picks
    // mock vs real based on VITE_SCHEMA_VALIDATION_USE_MOCK (default: mock).
    await new Promise((r) => setTimeout(r, 350));
    const result = await runSchemaValidation({
      selectedSites,
      siteCapabilities,
      sites,
      processingType,
      source: "pre_check",
    });
    setSchemaPreCheck({
      signature: currentSignature,
      result,
      status: "idle",
    });
    setShowDetails(false);
  };

  const SectionLabel = ({ children }) => (
    <Typography
      variant="caption"
      sx={{
        color: colors.grey[500],
        fontWeight: 700,
        fontSize: "0.6rem",
        textTransform: "uppercase",
        letterSpacing: 0.6,
        display: "block",
      }}
    >
      {children}
    </Typography>
  );

  const renderHeader = () => (
    <Typography sx={{ color: 'var(--fx-text-secondary)', fontSize: '0.75rem', lineHeight: 1.45, maxWidth: 560 }}>
      Advisory check that previews per-site schema compatibility. Final validation also runs on Start.
    </Typography>
  );

  // Unified status pills (1.4) via the shared StatusBadge. Semantic variant
  // carries the meaning; identical size/shape/treatment across every route and
  // correct in light + dark.
  const statusChip = (() => {
    switch (state) {
      case "not_ready":
        return <StatusBadge variant="warning" label="Selection incomplete" dot />;
      case "not_checked":
        return <StatusBadge variant="neutral" label="Not checked yet" dot />;
      case "checking":
        return <StatusBadge variant="neutral" label="Checking…" dot />;
      case "passed":
        return <StatusBadge variant="success" label="Pre-check passed" dot />;
      case "warn":
        return <StatusBadge variant="warning" label="Passed with warnings" dot />;
      case "failed":
        return <StatusBadge variant="danger" label="Found blocking issues" dot />;
      case "stale":
        return <StatusBadge variant="warning" label="Selection changed since last check" dot />;
      default:
        return null;
    }
  })();

  const hasResult = state === "passed" || state === "warn" || state === "failed";
  const buttonLabel =
    state === "checking" ? "Checking…" : hasResult ? "Re-check" : "Run schema pre-check";

  const buttonDisabled = state === "not_ready" || state === "checking";

  const helperText = (() => {
    switch (state) {
      case "not_ready":
        return "Complete each selected site with a dataset capability before running a schema pre-check.";
      case "not_checked":
        return "Run a pre-check now, or continue, final validation will run before training.";
      case "passed":
        return "All selected site datasets align with the expected schema. Final validation will run again before training starts.";
      case "warn":
        return "Some sites raised non-blocking warnings (e.g. extra columns ignored, missing label class). Training may still proceed, review the details below.";
      case "failed":
        return "Some sites have blocking schema issues (e.g. missing required feature, label out of range). Resolve them, or review the details below.";
      case "stale":
        return "Run the pre-check again, or continue, final validation will run before training.";
      case "checking":
        return "Checking selected site datasets…";
      default:
        return null;
    }
  })();

  return (
    <Card sx={{ mb: 2.5, p: 2.5, border: '1px solid var(--fx-border)', borderRadius: 2, boxShadow: 'var(--fx-shadow-sm)', bgcolor: 'var(--fx-paper)' }}>
      <Box>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2} sx={{ mb: 1.75 }}>
          <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                position: 'relative',
                width: 32, height: 32, borderRadius: '10px', flexShrink: 0,
                background: 'linear-gradient(135deg, #0087B4 0%, #1B365D 100%)',
                color: '#ffffff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.8125rem', fontWeight: 700, fontFamily: 'var(--fx-font-mono)',
                boxShadow: '0 4px 10px rgba(0, 135, 180, 0.25)',
                '&::after': {
                  content: '""', position: 'absolute', inset: -3,
                  borderRadius: '13px', border: '1px solid rgba(0, 135, 180, 0.20)',
                },
              }}
            >
              3
            </Box>
            <Box sx={{ minWidth: 0, flex: 1, pt: 0.25 }}>
              <Typography sx={{ color: 'var(--fx-text-muted)', fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', lineHeight: 1, mb: 0.4 }}>
                Step 3
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexWrap: 'wrap' }}>
                <Typography sx={{ fontWeight: 700, color: 'var(--fx-text-primary)', fontSize: '0.9375rem', lineHeight: 1.2, letterSpacing: '-0.005em' }}>
                  Schema pre-check
                </Typography>
                <Chip
                  size="small"
                  label="Optional"
                  sx={{
                    height: 18,
                    fontSize: '0.625rem',
                    fontWeight: 600,
                    bgcolor: 'var(--fx-subtle)',
                    color: 'var(--fx-text-secondary)',
                    border: '1px solid var(--fx-border)',
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                  }}
                />
              </Stack>
              <Box sx={{ mt: 0.25 }}>{renderHeader()}</Box>
            </Box>
          </Stack>
          {statusChip}
        </Stack>

        {/* Summary grid: Expected schema · Selected count · Last run */}
        <Box
          sx={{
            mt: 1.5,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
            gap: 2,
            p: 1.5,
            bgcolor: "var(--fx-subtle)",
            border: "1px solid var(--fx-border)",
            borderRadius: 1.5,
          }}
        >
          <Box>
            <SectionLabel>Expected schema</SectionLabel>
            <Typography
              sx={{
                color: "var(--fx-text-strong)",
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                fontSize: "0.8rem",
                fontWeight: 600,
                mt: 0.35,
                wordBreak: "break-word",
              }}
            >
              {expectedSchema || "Not set"}
            </Typography>
          </Box>
          <Box>
            <SectionLabel>Selected site datasets</SectionLabel>
            <Typography sx={{ color: "var(--fx-text-strong)", fontWeight: 600, fontSize: "0.85rem", mt: 0.35 }}>
              {completed.length} selected
            </Typography>
          </Box>
          <Box>
            <SectionLabel>Last run</SectionLabel>
            <Typography sx={{ color: "var(--fx-text-strong)", fontWeight: 600, fontSize: "0.85rem", mt: 0.35 }}>
              {schemaPreCheck?.result?.ranAt
                ? new Date(schemaPreCheck.result.ranAt).toLocaleTimeString()
                : "Not set"}
            </Typography>
          </Box>
        </Box>

        {helperText && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 2,
              color: "var(--fx-text-secondary)",
              fontSize: "0.75rem",
              lineHeight: 1.5,
            }}
          >
            {helperText}
          </Typography>
        )}

        <Stack direction="row" spacing={1.5} sx={{ mt: 2.5, flexWrap: "wrap", gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            disabled={buttonDisabled}
            startIcon={hasResult ? <RestartAltIcon /> : <RuleIcon />}
            onClick={runPreCheck}
          >
            {buttonLabel}
          </Button>
          {hasResult && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowDetails((v) => !v)}
            >
              {showDetails ? "Hide details" : "View details"}
            </Button>
          )}
        </Stack>

        <Collapse in={showDetails} timeout="auto" unmountOnExit>
          {schemaPreCheck?.result && (
            <Box sx={{ mt: 2.5 }}>
              <Divider sx={{ mb: 1.5 }} />
              <Alert
                severity={
                  schemaPreCheck.result.overall === "fail"
                    ? "error"
                    : schemaPreCheck.result.overall === "warn"
                    ? "warning"
                    : "success"
                }
                variant="outlined"
                sx={{ mb: 1.5 }}
              >
                {schemaPreCheck.result.summary}
              </Alert>
              <Stack spacing={0.75}>
                {schemaPreCheck.result.siteResults.map((r) => {
                  const sev = r.status || (r.passed ? "pass" : "fail");
                  // Token-based semantic tints (rgba over the surface) so the
                  // per-site rows read correctly in light AND dark (1.1/1.4).
                  const tone =
                    sev === "fail"
                      ? { bg: "rgba(220, 38, 38, 0.12)", border: "rgba(220, 38, 38, 0.30)", fg: "var(--fx-danger-text)" }
                      : sev === "warn"
                      ? { bg: "rgba(217, 119, 6, 0.12)", border: "rgba(217, 119, 6, 0.30)", fg: "var(--fx-warning-text)" }
                      : { bg: "rgba(22, 163, 74, 0.12)", border: "rgba(22, 163, 74, 0.30)", fg: "var(--fx-success-text)" };
                  const Icon =
                    sev === "fail"
                      ? ErrorOutlineIcon
                      : sev === "warn"
                      ? WarningAmberIcon
                      : CheckCircleIcon;
                  return (
                    <Box
                      key={`${r.site_id}::${r.capability_id}`}
                      sx={{
                        px: 1,
                        py: 0.75,
                        bgcolor: tone.bg,
                        borderRadius: 1,
                        border: `1px solid ${tone.border}`,
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Icon sx={{ fontSize: 16, color: tone.fg }} />
                        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: "var(--fx-text-strong)" }} noWrap>
                            {r.site_display_name}
                          </Typography>
                          {/* dev-0.7.10 — explicit per-site status chip
                              (PASS / WARN / FAIL) next to the site name.
                              Without this the row's severity was only
                              encoded in background color + icon, which
                              users found ambiguous when a site had only
                              one warn-level finding (e.g. TGH on BRCA
                              with just SCHEMA_FILE_IS_MOCK). */}
                          <Chip
                            size="small"
                            label={
                              sev === "pass" ? "PASS" :
                              sev === "warn" ? "WARN" : "FAIL"
                            }
                            sx={{
                              height: 18,
                              fontSize: "0.6rem",
                              fontWeight: 800,
                              letterSpacing: 0.5,
                              bgcolor: tone.bg,
                              color: tone.fg,
                              border: `1px solid ${tone.border}`,
                            }}
                          />
                        </Box>
                        <Tooltip title="capability_id">
                          <Typography
                            variant="caption"
                            sx={{ fontFamily: "monospace", color: colors.grey[500], fontSize: "0.65rem" }}
                          >
                            {r.capability_id}
                          </Typography>
                        </Tooltip>
                      </Stack>
                      {r.findings?.length > 0 && (
                        <Stack spacing={0.5} sx={{ mt: 0.75, pl: 3 }}>
                          {/* dev-0.7.10 — fail-first sort + friendly
                              title/message via CODE_FRIENDLY_OVERRIDES
                              (so e.g. backend's literal "Schema file is
                              DEV/mock" surfaces as "DEV schema contract"
                              with a less-alarming explanation). */}
                          {sortFindingsBySeverity(r.findings).map((f, i) => {
                            const display = getFindingDisplay(f);
                            return (
                              <Box
                                key={`${f.code || 'finding'}-${i}`}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: 0.6,
                                  flexWrap: 'wrap',
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: f.severity === "fail" ? "var(--fx-danger-text)" : "var(--fx-warning-text)",
                                    fontSize: "0.7rem",
                                    lineHeight: 1.45,
                                    flex: 1,
                                    minWidth: 0,
                                  }}
                                >
                                  <strong>{display.title}:</strong> {display.message}
                                </Typography>
                                {/* Small mono code badge — appears only
                                    when backend emitted a `code`; mock
                                    validator path leaves it empty. */}
                                {f.code && (
                                  <Tooltip title={display.tooltip}>
                                    <Box
                                      component="span"
                                      sx={{
                                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                                        fontSize: "0.58rem",
                                        fontWeight: 700,
                                        letterSpacing: 0.3,
                                        color: f.severity === "fail" ? "var(--fx-danger-text)" : "var(--fx-warning-text)",
                                        bgcolor: "var(--fx-paper)",
                                        border: `1px solid ${f.severity === "fail" ? "rgba(220, 38, 38, 0.30)" : "rgba(217, 119, 6, 0.30)"}`,
                                        borderRadius: 0.5,
                                        px: 0.6,
                                        py: 0.1,
                                        cursor: 'help',
                                        flexShrink: 0,
                                      }}
                                    >
                                      {f.code}
                                    </Box>
                                  </Tooltip>
                                )}
                              </Box>
                            );
                          })}
                        </Stack>
                      )}
                    </Box>
                  );
                })}
              </Stack>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 1.5,
                  color: "var(--fx-text-secondary)",
                  fontSize: "0.65rem",
                }}
              >
                Prototype mock check, final backend validation will be required in production.
              </Typography>
            </Box>
          )}
        </Collapse>
      </Box>
    </Card>
  );
};

export default SchemaPreCheckPanel;
